import type { TOC } from '@ember/component/template-only';
import { concat } from '@ember/helper';
import { PrismBanner, PrismButton } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { not, or } from 'ember-truth-helpers';
import type MfaController from 'frontend-login/controllers/mfa';
import {
  AuthenticatorCode,
  LoginCard,
  MfaSelection,
  MfaVerificationCode,
} from 'password-form';

import styles from './mfa.module.css';

interface MfaSignature {
  controller: MfaController;
}

<template>
  <LoginCard @title={{@controller.title}} class={{styles.container}}>
    <:default>
      {{#if @controller.showError}}
        <div class="mb-600">
          <PrismBanner
            @bannerMessage={{@controller.errorMessage}}
            @bannerStyle="critical"
            @bannerTitle={{@controller.errorTitle}}
            @icon="warning"
            data-test-id="banner-error"
          />
        </div>
      {{/if}}

      {{#if @controller.showVerificationStep}}
        {{#if @controller.showCaptchaIntermediateScreen}}
          <div
            class="recaptcha"
            data-test-id="captcha-intermediate-screen"
            {{@controller.initializeRecaptcha}}
          ></div>
        {{else}}
          <MfaVerificationCode
            @initializeRecaptcha={{@controller.createCaptcha}}
            @isCaptchaInVerificationCodeEnabled={{@controller.isCaptchaInVerificationCodeEnabled}}
            @isVerificationCodeValid={{@controller.isVerificationCodeValid}}
            @mfaType={{@controller.mfaType}}
            @onResendVerificationCode={{@controller.onResendVerificationCode}}
            @setVerificationCode={{@controller.setVerificationCode}}
            @submitVerificationCodeWithEnter={{@controller.submitVerificationCodeWithEnter}}
            @value={{@controller.verificationValue}}
            @verificationCode={{@controller.verificationCode}}
          />
        {{/if}}
      {{else if @controller.showAuthenticatorCode}}
        <AuthenticatorCode
          @issuer={{@controller.model.email}}
          @qrCode={{@controller.totpSecret}}
        />
      {{else}}
        <MfaSelection
          @initializeRecaptcha={{@controller.createCaptcha}}
          @mfaType={{@controller.mfaType}}
          @onChangeMfaType={{@controller.onChangeMfaType}}
          @onCountryCodeChange={{@controller.onCountryCodeChange}}
          @onPhoneNumberChange={{@controller.onPhoneNumberChange}}
          @phoneNumber={{@controller.phoneNumber}}
          @selectedCountryCode={{@controller.selectedCountryCode}}
          @updateIsPhoneNumberValid={{@controller.updateIsPhoneNumberValid}}
        />
      {{/if}}

      <div class="mt-600 flex flex-col justify-between gap-200">
        <div
          class={{concat styles.continue-button " " "flex w-full justify-end"}}
        >
          {{#if @controller.showVerificationStep}}
            <PrismButton
              @isDisabled={{@controller.isContinueForVerificationDisabled}}
              @isLoading={{@controller.isContinueForVerificationLoading}}
              @label={{t "general.continue"}}
              @onClick={{@controller.onContinueForVerificationClicked}}
              class="w-full"
              data-test-id="verification-continue-button"
            />
          {{else}}
            <PrismButton
              @isDisabled={{@controller.isContinueForSelectionDisabled}}
              @isLoading={{@controller.isContinueForSelectionLoading}}
              @label={{t "general.continue"}}
              @onClick={{@controller.onContinueForSelectionClicked}}
              class="w-full"
              data-test-id="selection-continue-button"
            />
          {{/if}}
        </div>
        {{#unless @controller.mfaService.isLoginVerificationActive}}
          {{#if
            (or
              @controller.showVerificationStep @controller.showAuthenticatorCode
            )
          }}
            <PrismButton
              @buttonType="secondary"
              @label={{t "login.back"}}
              @onClick={{@controller.onBackButtonClicked}}
              class="w-full"
            />
          {{else if (not @controller.isUserMfaEnforced)}}
            <PrismButton
              @buttonType="ghost"
              @label={{t "general.set_up_later"}}
              @onClick={{@controller.onSetupLater}}
              class="w-full"
              data-test-id="setup-later-button"
            />
          {{/if}}
        {{/unless}}

        {{#unless @controller.mfaService.isLoginVerificationActive}}
          <p
            class={{concat "paragraph-300--regular mt-400" " " styles.feedback}}
          >
            {{t "mfa.contact_customer_care" htmlSafe=true}}
          </p>
        {{/unless}}
      </div>
    </:default>
  </LoginCard>
</template> satisfies TOC<MfaSignature>;
